import React, { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { isAdmin } from '../utils/roles';
import { FiHome, FiSearch, FiTool, FiLogOut, FiMenu, FiX, FiSettings, FiClipboard, FiShield, FiUsers, FiAlertTriangle, FiCheckSquare, FiHelpCircle, FiFolder, FiPhone, FiBell, FiArrowLeft, FiLock } from 'react-icons/fi';
import Logo from './Logo';
import OtpFirstLoginPrompt from './OtpFirstLoginPrompt';
import { playNotification } from '../utils/sounds';

import { useSite } from '../context/SiteContext';
import { useLanguage } from '../context/LanguageContext';
import { APP_VERSION, COPYRIGHT_YEAR } from '../version';

export default function Layout() {
  const { user, logout, authFetch, maintenance } = useAuth();
  const site = useSite();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [myWorkCount, setMyWorkCount] = useState(0);
  const [supervisorCount, setSupervisorCount] = useState(0);
  const [serverTime, setServerTime] = useState(null);
  const [teamName, setTeamName] = useState('');
  const [phoneRequired, setPhoneRequired] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => { if (isMobile) setSidebarOpen(false); setShowNotifications(false); }, [location.pathname]);

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    if (isMobile && sidebarOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isMobile, sidebarOpen]);

  useEffect(() => {
    const fetchCount = () => {
      const now = new Date();
      const localDate = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      const todayStr = localDate(now);
      const dow = now.getDay();
      const monOff = dow === 0 ? -6 : 1 - dow;
      const mon = new Date(now.getFullYear(), now.getMonth(), now.getDate() + monOff);
      const sun = new Date(now.getFullYear(), now.getMonth(), now.getDate() + monOff + 6);
      const ws = localDate(mon);
      const we = localDate(sun);
      authFetch(`/api/issues/my-work?week_start=${ws}&week_end=${we}&today=${todayStr}`, { silent: true })
        .then(r => r.ok ? r.json() : { data: [] })
        .then(res => {
          const data = res && res.data ? res.data : [];
          setMyWorkCount(data.filter(i => !i.op_final_confirmed).length);
        })
        .catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 15000);
    return () => clearInterval(interval);
  }, [location.pathname]);

  // Fetch supervisor panel counts
  const prevSupervisorCount = useRef(-1);
  const playAlertSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      // Two-tone alert: ascending
      osc.frequency.setValueAtTime(523, ctx.currentTime);
      osc.frequency.setValueAtTime(659, ctx.currentTime + 0.12);
      osc.frequency.setValueAtTime(784, ctx.currentTime + 0.24);
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.setValueAtTime(0.2, ctx.currentTime + 0.3);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
    } catch {}
  };

  useEffect(() => {
    if (!user?.permissions?.includes('supervisor_panel') && !user?.permissions?.includes('admin_panel')) return;
    const fetchSupervisorCount = () => {
      const n = new Date(); const td = `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`;
      authFetch(`/api/issues/supervisor-counts?today=${td}`, { silent: true })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data) {
            const newCount = data.total || 0;
            if (newCount > prevSupervisorCount.current && prevSupervisorCount.current >= 0) {
              playAlertSound();
            }
            prevSupervisorCount.current = newCount;
            setSupervisorCount(newCount);
          }
        })
        .catch(() => {});
    };
    fetchSupervisorCount();
    const interval = setInterval(fetchSupervisorCount, 15000);
    return () => clearInterval(interval);
  }, [location.pathname, user?.role]);

  // Fetch server time
  useEffect(() => {
    const fetchTime = () => {
      fetch('/api/server-time').then(r => r.json()).then(d => setServerTime(d.time)).catch(() => {});
    };
    fetchTime();
    const interval = setInterval(fetchTime, 60000);
    return () => clearInterval(interval);
  }, []);

  // Fetch team name
  useEffect(() => {
    authFetch('/api/users/my-team-info').then(r => r.ok ? r.json() : null).then(d => { if (d?.team_name) setTeamName(d.team_name); }).catch(() => {});
  }, []);

  // Poll notification unread count
  const prevUnreadCount = useRef(-1);
  useEffect(() => {
    const fetchCount = () => {
      authFetch('/api/notifications/unread-count', { silent: true })
        .then(r => r.json())
        .then(data => {
          const newCount = data.count || 0;
          if (newCount > prevUnreadCount.current && prevUnreadCount.current >= 0) {
            playNotification();
          }
          prevUnreadCount.current = newCount;
          setUnreadCount(newCount);
        })
        .catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 15000);
    return () => clearInterval(interval);
  }, []);

  const loadNotifications = () => {
    setNotifLoading(true);
    authFetch('/api/notifications')
      .then(r => r.json())
      .then(data => { setNotifications(Array.isArray(data) ? data : []); setNotifLoading(false); })
      .catch(() => setNotifLoading(false));
  };

  const markAllNotifRead = async () => {
    await authFetch('/api/notifications/read-all', { method: 'PATCH' }).catch(() => {});
    setNotifications(prev => prev.filter(n => false));
    setUnreadCount(0);
  };

  const markNotifRead = async (notif) => {
    if (!notif.is_read) {
      await authFetch(`/api/notifications/${notif.id}/read`, { method: 'PATCH' }).catch(() => {});
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: 1 } : n));
      setUnreadCount(c => Math.max(0, c - 1));
    }
  };

  // Check if phone number is required
  useEffect(() => {
    if (user && !user.phone_number) setPhoneRequired(true);
    else setPhoneRequired(false);
  }, [user?.phone_number]);

  const navItems = [
    { to: '/', icon: FiHome, label: t('nav.dashboard'), end: true },
    { to: '/my-work', icon: FiClipboard, label: t('nav.myWork') },
    { to: '/assets', icon: FiSearch, label: t('nav.assetLookup') },
    { to: '/work-orders', icon: FiTool, label: t('nav.workOrders') },
    { to: '/projects', icon: FiFolder, label: t('nav.projects') },
    { to: '/credentials', icon: FiLock, label: t('nav.credentials') },
  ];

  const formatServerTime = (isoStr) => {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const formatServerDate = (isoStr) => {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const NOTIF_TYPE_CONFIG = {
    assignment: { color: '#2563eb', label: 'Assignment' },
    completed: { color: '#16a34a', label: 'Completed' },
    comment: { color: '#0ea5e9', label: 'Comment' },
    overdue: { color: '#dc2626', label: 'Overdue' },
  };

  const showSidebar = !isMobile || sidebarOpen;
  const avatarUrl = user?.avatar ? `/api/users/avatar/${user.avatar}` : null;
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : parts[0][0].toUpperCase();
  };
  const initials = getInitials(user?.name);
  const isMaintenanceMode = maintenance?.maintenance;
  const isUserAdmin = isAdmin(user);

  // Show maintenance page for non-admin users
  if (isMaintenanceMode && !isUserAdmin) {
    const estEnd = maintenance.estimated_end ? new Date(maintenance.estimated_end) : null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--bg-primary)', padding: 40, textAlign: 'center' }}>
        <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(37,99,235,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
          <FiAlertTriangle size={40} color="#2563eb" />
        </div>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>System Maintenance</h1>
        <p style={{ fontSize: 15, color: 'var(--text-secondary)', maxWidth: 460, margin: '0 0 20px', lineHeight: 1.6 }}>
          {maintenance.message || 'The system is currently under maintenance. Please try again later.'}
        </p>
        {estEnd && (
          <div style={{ padding: '12px 20px', borderRadius: 10, background: 'var(--bg-card)', border: '1px solid var(--border)', fontSize: 14, color: 'var(--text-primary)' }}>
            Estimated completion: <strong>{estEnd.toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}</strong>
          </div>
        )}
        <button onClick={logout} style={{ marginTop: 24, padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
          Sign Out
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <AnimatePresence>
        {isMobile && sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setSidebarOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 40 }}
          />
        )}
      </AnimatePresence>

      <aside style={{
        width: 260, background: 'var(--sidebar-bg)', borderRight: 'none',
        display: 'flex', flexDirection: 'column', position: 'fixed', top: 0, bottom: 0, left: 0, zIndex: 50,
        transform: showSidebar ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.3s ease',
        boxShadow: '2px 0 12px rgba(0,0,0,0.1)'
      }}>
        <div style={{ padding: '24px 20px', paddingTop: 'calc(env(safe-area-inset-top, 0px) + 24px)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {site?.logoUrl ? (
              <img src={site.logoUrl} alt="" style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'contain' }} />
            ) : (
              <Logo size={40} light />
            )}
            <div>
              <div style={{ fontWeight: 700, fontSize: 18, letterSpacing: '-0.5px', color: '#fff' }}>{site?.site_name || 'Asset Logic'}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Powered by Asset Logic</div>
            </div>
            {isMobile && (
              <button onClick={() => setSidebarOpen(false)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', padding: 4 }}>
                <FiX size={20} />
              </button>
            )}
          </div>
          {serverTime && (
            <div style={{ textAlign: 'center', marginTop: 10, fontSize: 11, color: 'rgba(255,255,255,0.45)', fontWeight: 500 }}>
              {formatServerDate(serverTime)} &middot; {formatServerTime(serverTime)}
            </div>
          )}
        </div>

        <nav style={{ flex: 1, padding: '12px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === '/'}
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                borderRadius: 10, fontSize: 14, fontWeight: 500, transition: 'all 0.2s',
                background: isActive ? 'var(--sidebar-active)' : 'transparent',
                color: isActive ? 'var(--sidebar-text-active)' : 'var(--sidebar-text)',
                textDecoration: 'none'
              })}
            >
              <item.icon size={18} />
              <span style={{ flex: 1 }}>{item.label}</span>
              {item.to === '/my-work' && myWorkCount > 0 && (
                <span style={{
                  minWidth: 20, height: 20, borderRadius: 10, background: '#ef4444',
                  color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', padding: '0 5px'
                }}>{myWorkCount}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div style={{ padding: '10px 16px 16px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <NavLink to="/my-team" style={({ isActive }) => ({
            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, padding: '10px 12px',
            borderRadius: 10, textDecoration: 'none', transition: 'all 0.2s',
            background: isActive ? 'var(--sidebar-active)' : 'transparent',
            color: isActive ? '#fff' : 'rgba(255,255,255,0.5)',
            fontSize: 13, fontWeight: 600
          })}>
            <FiUsers size={16} />
            <span>{t('nav.myTeam')}</span>
          </NavLink>
          <NavLink to="/help" style={({ isActive }) => ({
            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, padding: '10px 12px',
            borderRadius: 10, textDecoration: 'none', transition: 'all 0.2s',
            background: isActive ? 'var(--sidebar-active)' : 'transparent',
            color: isActive ? '#fff' : 'rgba(255,255,255,0.5)',
            fontSize: 13, fontWeight: 600
          })}>
            <FiHelpCircle size={16} />
            <span>{t('nav.helpCentre')}</span>
          </NavLink>
          {(user?.permissions?.includes('supervisor_panel') || user?.permissions?.includes('admin_panel')) && (
            <NavLink to="/supervisor" style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, padding: '10px 12px',
              borderRadius: 10, textDecoration: 'none', transition: 'all 0.2s',
              background: isActive ? 'var(--sidebar-active)' : 'transparent',
              color: isActive ? '#fff' : 'rgba(255,255,255,0.5)',
              fontSize: 13, fontWeight: 600
            })}>
              <FiCheckSquare size={16} />
              <span>{t('nav.supervisor')}</span>
              {supervisorCount > 0 && (
                <span style={{
                  minWidth: 20, height: 20, borderRadius: 10, background: '#ef4444',
                  color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', padding: '0 5px', marginLeft: 'auto'
                }}>{supervisorCount}</span>
              )}
            </NavLink>
          )}
          {isAdmin(user) && (
            <NavLink to="/admin" style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, padding: '10px 12px',
              borderRadius: 10, textDecoration: 'none', transition: 'all 0.2s',
              background: isActive ? 'var(--sidebar-active)' : 'transparent',
              color: isActive ? '#fff' : 'rgba(255,255,255,0.5)',
              fontSize: 13, fontWeight: 600
            })}>
              <FiShield size={16} />
              <span>{t('nav.adminPanel')}</span>
            </NavLink>
          )}
          <NavLink to="/settings" style={({ isActive }) => ({
            display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, padding: '10px 12px',
            borderRadius: 10, textDecoration: 'none', transition: 'all 0.2s',
            background: isActive ? 'var(--sidebar-active)' : 'transparent',
            cursor: 'pointer'
          })}>
            {avatarUrl ? (
              <img src={avatarUrl} alt="" style={{
                width: 36, height: 36, borderRadius: '50%', flexShrink: 0, objectFit: 'cover'
              }} />
            ) : (
              <div style={{
                width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                background: user?.avatar_color || 'linear-gradient(135deg, #2563eb, #3b82f6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 15, fontWeight: 700, color: '#fff'
              }}>
                {initials}
              </div>
            )}
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#fff', lineHeight: 1.3 }}>{user?.name}</span>
                {unreadCount > 0 && (
                  <span onClick={e => { e.preventDefault(); e.stopPropagation(); setShowNotifications(true); loadNotifications(); }} style={{
                    minWidth: 18, height: 18, borderRadius: 9, background: '#ef4444',
                    color: '#fff', fontSize: 10, fontWeight: 700, display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center', padding: '0 4px', cursor: 'pointer'
                  }}>{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
              </div>
              {user?.employee_id && (
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', fontWeight: 500, letterSpacing: '0.5px', marginTop: 2 }}>
                  ID: {user.employee_id}
                </div>
              )}
              {teamName && (
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', fontWeight: 500, marginTop: 1 }}>
                  {teamName}
                </div>
              )}
            </div>
            <FiSettings size={14} color="rgba(255,255,255,0.4)" style={{ flexShrink: 0 }} />
          </NavLink>
          <button onClick={logout} style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)',
            background: 'transparent', color: 'rgba(255,255,255,0.7)', cursor: 'pointer',
            fontSize: 13, fontWeight: 500, transition: 'all 0.2s'
          }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.2)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.4)'; e.currentTarget.style.color = '#fca5a5'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
          >
            <FiLogOut size={14} /> {t('nav.signOut')}
          </button>
          <div style={{ textAlign: 'center', marginTop: 8, fontSize: 10, color: 'rgba(255,255,255,0.3)', lineHeight: 1.4 }}>
            &copy; {COPYRIGHT_YEAR} {site?.site_name || 'Asset Logic'}<br />v{APP_VERSION}
          </div>
        </div>
      </aside>

      <div style={{ flex: 1, marginLeft: isMobile ? 0 : 260, minWidth: 0, background: 'var(--bg-primary)' }}>
        {isMobile && (
          <div style={{
            display: 'flex', padding: '12px 16px', paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)', background: 'var(--bg-card)',
            borderBottom: '1px solid var(--border)', alignItems: 'center', justifyContent: 'space-between',
            position: 'sticky', top: 0, zIndex: 30, boxShadow: 'var(--shadow)'
          }}>
            <button onClick={() => setSidebarOpen(true)} style={{
              background: 'none', border: 'none', color: 'var(--text-primary)', cursor: 'pointer', padding: 4
            }}>
              <FiMenu size={24} />
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {site?.logoUrl ? (
                <img src={site.logoUrl} alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'contain' }} />
              ) : (
                <Logo size={28} />
              )}
              <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--text-primary)' }}>{site?.site_name || 'Asset Logic'}</span>
            </div>
            <div style={{ width: 32 }} />
          </div>
        )}

        {isMaintenanceMode && isUserAdmin && (
          <div style={{ padding: '10px 16px', background: 'rgba(37,99,235,0.1)', borderBottom: '1px solid rgba(37,99,235,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#2563eb' }}>
            <FiAlertTriangle size={14} /> Maintenance mode is ON
          </div>
        )}

        {showNotifications ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            style={{ padding: isMobile ? '16px' : '24px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button onClick={() => setShowNotifications(false)}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12 }}>
                  <FiArrowLeft size={14} /> Back
                </button>
                <h1 style={{ margin: 0, fontSize: 'clamp(20px, 5vw, 28px)', fontWeight: 800, letterSpacing: '-0.5px', color: 'var(--text-primary)' }}>Notifications</h1>
              </div>
              {unreadCount > 0 && (
                <button onClick={markAllNotifRead}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--accent)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                  Mark all read
                </button>
              )}
            </div>
            {notifLoading ? (
              <div>{[...Array(5)].map((_, i) => <div key={i} className="skeleton" style={{ height: 72, borderRadius: 12, marginBottom: 8 }} />)}</div>
            ) : notifications.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
                <FiBell size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
                <div style={{ fontSize: 15, fontWeight: 600 }}>No notifications yet</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {notifications.map(notif => {
                  const cfg = NOTIF_TYPE_CONFIG[notif.type] || NOTIF_TYPE_CONFIG.assignment;
                  return (
                    <motion.div key={notif.id} whileHover={{ y: -1, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}
                      onClick={() => { markNotifRead(notif); if (notif.link) { setShowNotifications(false); navigate(notif.link.replace(/^\/app/, '') || '/'); } }}
                      style={{
                        padding: '14px 18px', borderRadius: 12, border: '1px solid var(--border)',
                        background: notif.is_read ? 'var(--bg-card)' : 'rgba(37,99,235,0.04)',
                        cursor: notif.link ? 'pointer' : 'default', transition: 'all 0.2s',
                        display: 'flex', gap: 14, alignItems: 'flex-start'
                      }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                        background: `${cfg.color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <FiBell size={18} color={cfg.color} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{notif.title}</span>
                          {!notif.is_read && (
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />
                          )}
                        </div>
                        {notif.message && (
                          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.4, marginBottom: 4 }}>{notif.message}</div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                          <span style={{ padding: '1px 6px', borderRadius: 4, background: `${cfg.color}10`, color: cfg.color, fontWeight: 600, fontSize: 10 }}>{cfg.label}</span>
                          <span>{timeAgo(notif.created_at)}</span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.main
              key={location.pathname}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25 }}
              style={{ padding: isMobile ? '16px' : '24px' }}
            >
              <Outlet />
              <OtpFirstLoginPrompt />
            </motion.main>
          </AnimatePresence>
        )}
      </div>

      {/* Phone Number Required Overlay */}
      {phoneRequired && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 32, width: '100%', maxWidth: 420, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(37,99,235,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <FiPhone size={28} color="#2563eb" />
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>Phone Number Required</h2>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, lineHeight: 1.5 }}>
              Please add your phone number to continue using the system.
            </p>
            <div style={{ position: 'relative', marginBottom: 16 }}>
              <FiPhone size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input type="tel" value={phoneInput} onChange={e => {
                let digits = e.target.value.replace(/[^\d]/g, '').slice(0, 11);
                if (!digits) { setPhoneInput(''); return; }
                // Format: +XX XXX XXX XXX
                let formatted = '+';
                if (digits.length <= 2) { formatted += digits; }
                else {
                  formatted += digits.slice(0, 2);
                  let rest = digits.slice(2);
                  for (let i = 0; i < rest.length; i += 3) {
                    formatted += ' ' + rest.slice(i, i + 3);
                  }
                }
                setPhoneInput(formatted);
              }} placeholder="+61 455 112 578"
                style={{ width: '100%', padding: '12px 14px 12px 40px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <button disabled={!phoneInput.trim() || phoneInput.trim().length < 8 || phoneSaving}
              onClick={async () => {
                setPhoneSaving(true);
                try {
                  const res = await authFetch('/api/users/profile/phone', { method: 'PATCH', body: JSON.stringify({ phone_number: phoneInput.trim() }) });
                  if (res.ok) {
                    setPhoneRequired(false);
                    // Refresh user data
                    window.location.reload();
                  }
                } catch (e) {}
                setPhoneSaving(false);
              }}
              style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: (!phoneInput.trim() || phoneInput.trim().length < 8) ? '#94a3b8' : '#2563eb', color: '#fff', fontSize: 14, fontWeight: 600, cursor: (!phoneInput.trim() || phoneInput.trim().length < 8) ? 'not-allowed' : 'pointer' }}>
              {phoneSaving ? 'Saving...' : 'Save Phone Number'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
